import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { getAllBlogPosts, getBlogPost } from "@/services/blog";
import { BlogPost } from "@/components/BlogPost";
import { useEffect, useState } from "react";

// SEO Component for Blog
const BlogSEO = ({ post }: { post?: any }) => {
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | YakStak Blog`;
      
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.excerpt || post.description);
      }
      
      // Add blog post structured data
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt || post.description,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "datePublished": post.date,
        "publisher": {
          "@type": "Organization",
          "name": "YakStak",
          "url": "https://yakstak.app"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://yakstak.app/blog/${post.slug}`
        }
      };
      
      let script = document.getElementById('blog-structured-data');
      if (script) {
        script.textContent = JSON.stringify(structuredData);
      } else {
        script = document.createElement('script');
        script.id = 'blog-structured-data';
        (script as HTMLScriptElement).type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    } else {
      document.title = 'Blog - User Research Insights & Tips | YakStak';
      
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Expert insights, tips, and best practices for user research, product discovery, and customer development. Learn how to turn user interviews into product decisions.');
      }
    }
    
    return () => {
      const script = document.getElementById('blog-structured-data');
      if (script) {
        script.remove();
      }
    };
  }, [post]);
  
  return null;
};

const Blog = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [currentPost, setCurrentPost] = useState<any>(null);

  useEffect(() => {
    const posts = getAllBlogPosts();
    setBlogPosts(posts);
    
    if (slug) {
      const post = getBlogPost(slug);
      setCurrentPost(post);
    }
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // If viewing a specific blog post
  if (slug && currentPost) {
    return (
      <>
        <BlogSEO post={currentPost} />
        <BlogPost post={currentPost} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-background">
      <BlogSEO />
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" role="banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <img className="w-8 h-8" src="/yak-stak-logo.png" alt="YakStak Logo" />
              <span className="text-xl font-bold text-foreground">YakStak</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12" role="main">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            YakStak Blog
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Insights, tips, and best practices for user research and product discovery
          </p>
        </div>

        {/* Featured Post */}
        {blogPosts.find(post => post.featured) && (
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-6">Featured Post</h2>
            {blogPosts.filter(post => post.featured).map((post, index) => (
              <Card key={index} className="bg-gradient-subtle border-primary/20">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge>{post.category}</Badge>
                    <Badge variant="secondary">Featured</Badge>
                  </div>
                  <CardTitle className="text-2xl">{post.title}</CardTitle>
                  <CardDescription className="text-lg">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {post.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.date)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/blog/${post.slug}`)}>Read More</Button>
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        {/* All Posts */}
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-6">All Posts</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post, index) => (
              <Card key={index} className="h-full">
                <CardHeader>
                  <Badge className="w-fit mb-2">{post.category}</Badge>
                  <CardTitle className="text-lg">{post.title}</CardTitle>
                  <CardDescription>{post.excerpt}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {post.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {formatDate(post.date)}
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/blog/${post.slug}`)}>
                    Read More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="mt-16 text-center">
          <Card className="bg-gradient-subtle border-primary/20">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Stay Updated
              </h3>
              <p className="text-muted-foreground mb-6">
                Join our waitlist to get notified when new blog posts are published and when YakStak launches
              </p>
              <Button onClick={() => navigate("/#cta")}>
                Join the Waitlist
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default Blog;